import { useMemo, useState } from "react";
import { KeyRound, Trash2 } from "lucide-react";

import { HealthlinkDoctorDetailsModal } from "components/HealthlinkCredentialsModal/HealthlinkDoctorDetailsModal/HealthlinkDoctorDetailsModal";
import { HealthLinkPracticeModal } from "components/HealthlinkCredentialsModal/HealthLinkPracticeModal/HealthLinkPracticeModal";
import { PCRSCredentialsModal } from "components/HealthlinkCredentialsModal/PCRSCredentialsModal/PCRSCredentialsModal";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { Notice, NoticeText } from "components/ui/Notice";
import { Pill, PILL_COLORS } from "components/ui/Pill";

import styles from "../../HealthlinkDetailsPage.module.css";
import { formatDate } from "../../utils/formatDate";

const ACTIVE_PILL = PILL_COLORS.statusSuccess;

const staffFirstName = (row) => row.staffMember?.firstName || "-";
const staffLastName = (row) => row.staffMember?.lastName || "-";

const DELETE_COPY = {
  practice: "Are you sure you want to delete this practice credential?",
  doctor: "Are you sure you want to delete this doctor credential?",
};

export const CredentialsTab = ({
  healthlinkPractice,
  healthlinkCredentials,
  healthlinkPracticeCredentials,
  pcrsCredentials,
  hasClinicDetailsSaved,
  hasGpDetailsSaves,
  onDeletePracticeCredential,
  onDeleteDoctorCredential,
  onTestLogin,
  isTestingLogin,
}) => {
  const [credentialToDelete, setCredentialToDelete] = useState(null);

  const description = hasGpDetailsSaves
    ? "You have already configured your Healthlink credentials. You can update them by clicking the buttons below."
    : hasClinicDetailsSaved
    ? "You have already configured your Healthlink certificate for your tenant. You still need to configure your doctor credentials."
    : "You can configure them by clicking the buttons below:";

  const hasPracticeCredentials =
    Array.isArray(healthlinkPracticeCredentials) &&
    healthlinkPracticeCredentials.length > 0;

  const practiceColumns = useMemo(
    () => [
      {
        id: "uploadedBy",
        header: "Uploaded By",
        width: "minmax(160px, 2fr)",
        render: (row) => `${staffFirstName(row)} ${staffLastName(row)}`,
      },
      {
        id: "createdAt",
        header: "Created At",
        width: "minmax(160px, 1fr)",
        // ISO since KIN-2174; formatted like the PCRS tab rather than shown raw.
        render: (row) => formatDate(row.createdAt),
      },
      {
        id: "status",
        header: "Status",
        width: "120px",
        wrap: true,
        render: () => <Pill {...ACTIVE_PILL}>Active</Pill>,
      },
    ],
    []
  );

  const doctorColumns = useMemo(
    () => [
      { id: "id", header: "ID", width: "80px" },
      {
        id: "firstName",
        header: "First Name",
        width: "minmax(140px, 1fr)",
        render: staffFirstName,
      },
      {
        id: "lastName",
        header: "Last Name",
        width: "minmax(140px, 1fr)",
        render: staffLastName,
      },
      {
        id: "login",
        header: "Login",
        width: "minmax(160px, 2fr)",
        render: (row) => row.login || "-",
      },
      {
        id: "status",
        header: "Status",
        width: "120px",
        wrap: true,
        render: () => <Pill {...ACTIVE_PILL}>Active</Pill>,
      },
    ],
    []
  );

  const pcrsColumns = useMemo(
    () => [
      {
        id: "firstName",
        header: "First Name",
        width: "minmax(120px, 1fr)",
        render: staffFirstName,
      },
      {
        id: "lastName",
        header: "Last Name",
        width: "minmax(120px, 1fr)",
        render: staffLastName,
      },
      {
        id: "pcrsUsername",
        header: "PCRS Username",
        width: "minmax(140px, 1fr)",
        render: (row) => row.pcrsUsername || "-",
      },
      {
        id: "certificateFileName",
        header: "Certificate File",
        width: "minmax(160px, 1fr)",
        render: (row) => row.certificateFileName || "-",
      },
      {
        id: "createdAt",
        header: "Created At",
        width: "minmax(150px, 1fr)",
        render: (row) => formatDate(row.createdAt),
      },
      {
        id: "updatedAt",
        header: "Updated At",
        width: "minmax(150px, 1fr)",
        render: (row) => formatDate(row.updatedAt),
      },
    ],
    []
  );

  const practiceRowActions = useMemo(
    () => [
      {
        label: "Delete",
        icon: Trash2,
        onClick: (row) => setCredentialToDelete({ type: "practice", row }),
      },
    ],
    []
  );

  const doctorRowActions = useMemo(
    () => [
      {
        label: "Test login",
        icon: KeyRound,
        primary: true,
        disabled: (row) => !!isTestingLogin || !row.staffMember?.id,
        onClick: (row) => onTestLogin(row.staffMember?.id),
      },
      {
        label: "Delete",
        icon: Trash2,
        onClick: (row) => setCredentialToDelete({ type: "doctor", row }),
      },
    ],
    [isTestingLogin, onTestLogin]
  );

  const handleConfirmDelete = () => {
    if (credentialToDelete?.type === "practice") {
      onDeletePracticeCredential(credentialToDelete.row.id);
    } else {
      onDeleteDoctorCredential(credentialToDelete.row.id);
    }

    setCredentialToDelete(null);
  };

  return (
    <div className={styles.content}>
      <Notice tone="info" testId="healthlink-credentials-notice">
        <NoticeText isLead>
          Configure Healthlink for your account and tenant
        </NoticeText>
        <NoticeText>{description}</NoticeText>
      </Notice>

      <div className={styles.actions}>
        <HealthlinkDoctorDetailsModal
          buttonText="Configure doctor credentials"
          initialValues={healthlinkCredentials}
          isDisabled={!hasClinicDetailsSaved}
          buttonVariant="outline"
        />
        <HealthLinkPracticeModal
          buttonText="Configure practice certificate"
          initialValues={healthlinkPractice}
          buttonVariant="outline"
        />
        <PCRSCredentialsModal
          buttonText="Configure PCRS credentials"
          initialValues={null}
          buttonVariant="outline"
        />
      </div>

      {!!hasPracticeCredentials && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>
              Healthlink Practice Credentials
            </h4>
          </div>
          <DataTable
            columns={practiceColumns}
            data={healthlinkPracticeCredentials}
            getRowId={(row) => row.id}
            emptyTitle="No practice credentials found"
            emptyText="Upload a practice certificate and it will appear here."
            rowActions={practiceRowActions}
            rowActionsLabel="Practice credential actions"
          />
        </div>
      )}

      {!!hasClinicDetailsSaved && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h4 className={styles.sectionTitle}>
              Healthlink Doctor Credentials Saved
            </h4>
          </div>
          <DataTable
            columns={doctorColumns}
            data={healthlinkCredentials}
            getRowId={(row) => row.id}
            emptyTitle="No doctor credentials found"
            emptyText="Use Configure doctor credentials and they will appear here."
            rowActions={doctorRowActions}
            rowActionsLabel="Doctor credential actions"
          />
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h4 className={styles.sectionTitle}>PCRS Credentials Saved</h4>
        </div>
        <DataTable
          columns={pcrsColumns}
          data={Array.isArray(pcrsCredentials) ? pcrsCredentials : []}
          getRowId={(row) => row.doctorId}
          emptyTitle="No PCRS credentials found"
          emptyText='Click "Configure PCRS credentials" to add credentials.'
        />
      </div>

      <ConfirmDialog
        isOpen={!!credentialToDelete}
        onClose={() => setCredentialToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete credential"
        message={
          credentialToDelete ? DELETE_COPY[credentialToDelete.type] : ""
        }
        confirmLabel="Delete"
      />
    </div>
  );
};
